﻿import { SiteEntityGrid } from "@/Common/Base/SiteEntityGrid";
import { parseCriteria } from "@/Common/CriteriaExpression/CriteriaExpression";
import { microFormat } from "@/Common/Helpers/MicroFormat";
import { ClientEntityGrid, ClientEntityOptions } from "@/Data/ClientTable/ILGeneration/ClientEntityPage";
import { RowInfoService } from "@/ServerTypes/Data/RowInfoService";
import { nsData } from "@/ServerTypes/Namespaces";
import { Authorization, debounce, EntityDialog, Fluent, getTypeFullName, htmlEncode, ISetEditValue, LazyLoadHelper, PropertyDialog, resolveUrl, trimToNull, tryGetWidget, Widget, WidgetProps } from "@serenity-is/corelib";

export interface DetailsGridEditorProps {
    criteriaText?: string;
    rowIdentifier: string;
    gridTitle?: string;
    hideGridTitle?: boolean;
    gridReadOnly?: boolean;
    hideToolbar?: boolean;
    minHeight?: string;
    persistenceKey?: string;
    uniquePersistenceKey?: boolean;
}

export class DetailsGridEditor extends Widget<DetailsGridEditorProps> implements ISetEditValue {
    static override [Symbol.typeInfo] = this.registerEditor(nsData, [ISetEditValue]);

    protected ownerWidget: EntityDialog<any, any> | PropertyDialog<any, any> | null = null;
    protected formattedCriteria: string | null = null;
    protected loadCalled = false;

    protected constructor(props: WidgetProps<DetailsGridEditorProps>) {
        super(props);

        this.tryFindOwnerDialog();
    }

    setEditValue(): void {
        const load = () => {
            if (this.ownerWidget?.["entity"] != null) {
                this.createGrid();
                return;
            }

            requestAnimationFrame(load);
        }

        if (this.loadCalled)
            return;

        this.loadCalled = true;
        load();
    }

    protected async createGrid() {
        if (this.ownerWidget["isNew"]?.())
            return;

        const opt = this.options;

        const info = await RowInfoService.GetInfo({
            EntityId: opt.rowIdentifier
        });

        if (info.ReadPermission != null && !Authorization.hasPermission(info.ReadPermission))
            return;

        const el = Fluent("div");
        el.appendTo(this.domNode);
        el.style(x => x.minHeight = "1px");

        if (opt.minHeight != null)
            el.style(x => {
                if (typeof opt.minHeight == "string") {
                    if (opt.minHeight.endsWith("%")) {
                        const percentage = parseInt(opt.minHeight.substring(0, opt.minHeight.length - 1));
                        if (!isNaN(percentage)) {
                            x.minHeight = "300px";
                            this.updatePercentageHeight(percentage, el);
                            this.bindResizeForPercentageHeight(percentage, el);
                            LazyLoadHelper.executeOnceWhenShown(el, () => {
                                this.updatePercentageHeight(percentage, el);
                            });
                        }
                    } else if (opt.minHeight.endsWith("px"))
                        x.minHeight = opt.minHeight;
                    else
                        x.height = opt.minHeight + "px";

                    // backward compatibility
                } else if (typeof opt.minHeight == "number") {
                    x.height = `${opt.minHeight}px`
                }
            });

        LazyLoadHelper.executeOnceWhenShown(el, async () => {
            const criteria = this.getCriteriaFromParams();
            let criteriaObj: { criteria: any[], text: string } | null = null;
            if (criteria?.length > 0)
                criteriaObj = {
                    criteria: criteria,
                    text: this.formattedCriteria
                }

            let persistenceKey: string | undefined = undefined;
            if (trimToNull(opt.persistenceKey) != null) {
                persistenceKey = `DetailsGrid:CustomKey:${opt.persistenceKey}`;
            }

            if (opt.uniquePersistenceKey) {
                persistenceKey = `DetailsGrid:${getTypeFullName(this.ownerWidget.constructor)}:` +
                    `${this.options.rowIdentifier}:` +
                    `${this.domNode.closest<HTMLDivElement>(".field")?.dataset.itemname ?? ""}`
            }

            let grid: SiteEntityGrid<any, any>;
            if (info.Identifier != null) {
                grid = new ClientEntityGrid({
                    element: el,
                    module: "Ext",
                    identifier: info.Identifier,
                    idProperty: info.IdProperty,
                    nameProperty: info.NameProperty,
                    quickSearch: info.QuickSearch,
                    useQueryCriteria: false,
                    useCriteriaForNew: true,
                    overrideQueryCriteria: criteriaObj,
                    persistenceKey: persistenceKey
                } as ClientEntityOptions);
            } else if (info.GridImportPath != null) {
                const module = await import(resolveUrl(info.GridImportPath));
                let gridType = null;

                for (const key in module) {
                    if (module[key] instanceof Function && (module[key].prototype instanceof SiteEntityGrid || key.endsWith("Grid"))) {
                        gridType = module[key];
                        break;
                    }
                }

                if (gridType == null)
                    throw new Error("Invalid entity info.");

                grid = new gridType({
                    element: el,
                    useQueryCriteria: false,
                    useCriteriaForNew: true,
                    overrideQueryCriteria: criteriaObj,
                    persistenceKey: persistenceKey
                });
            } else {
                throw new Error("Invalid entity info.");
            }

            if (opt.gridTitle?.length > 0 && !opt.hideGridTitle)
                grid.setTitle(opt.gridTitle);

            if (opt.hideGridTitle)
                grid.element.findFirst(".grid-title > .title-text").hide();

            if (opt.hideToolbar)
                grid["toolbar"]?.element.children(":not(.grid-title)").forEach(x => Fluent(x).hide());

            if (opt.gridReadOnly)
                grid.set_readOnly(true);

            const categories = el.closest(".categories");
            const category = categories.children(".category");
            if (category.length == 1 && category[0].children.length === 1 && opt.minHeight == null) {
                [Fluent(category[0]), categories, this.getGridField(), this.element, el].forEach(e => e.style(x => x.height = "100%"));
            } else if (opt.minHeight == null) {
                el.style(x => x.height = "300px");
            }

            this.bindPersistence(grid);
        });
    }

    private updatePercentageHeight(percentage: number, el: Fluent<HTMLElement>) {
        const sourceHeight = el.closest("section.content").getNode()?.clientHeight === window.innerHeight ?
            el.closest<HTMLDivElement>(".categories").getNode()?.clientHeight :
            window.innerHeight - 50;

        if (sourceHeight != null) {
            el.style(x => {
                x.height = (sourceHeight * percentage / 100 - 8) + "px";
            });
            el.trigger("layout");
        }
    }

    private bindResizeForPercentageHeight(percentage: number, el: Fluent<HTMLElement>) {
        if (!this.ownerWidget)
            return;

        const debouncedHeight = debounce(() => this.updatePercentageHeight(percentage, el), 100);
        Fluent.on(window, `resize.${this.ownerWidget.uniqueName}`, () => debouncedHeight());

        const originalArrange = this.ownerWidget.arrange;
        if (!originalArrange)
            return;

        this.ownerWidget.arrange = () => {
            this.updatePercentageHeight(percentage, el);
            originalArrange.apply(this.ownerWidget);
        };
    }

    public override destroy() {
        if (this.ownerWidget)
            Fluent.off(window, `.${this.ownerWidget.uniqueName}`);
        super.destroy();
    }

    protected bindPersistence(grid: SiteEntityGrid<any, any>) {
        const activePersistenceKey = htmlEncode(grid["getPersistenceKey"]?.call(grid) ?? "");

        this.ownerWidget.element.on(`restore-detailsgrid-columns-${activePersistenceKey}`, (e) => {
            const targetEl = e.target as HTMLElement;
            if (targetEl.dataset.gridsettingpersistence == grid.idPrefix) {
                delete targetEl.dataset.gridsettingpersistence;
                return;
            }

            grid["restoreSettings"]?.();
        });

        const oldPersistSettings = grid["persistSettings"];
        grid["persistSettings"] = () => {
            oldPersistSettings?.call(grid);
            this.ownerWidget.domNode.dataset.gridsettingpersistence = grid.idPrefix;
            this.ownerWidget.element.trigger(`restore-detailsgrid-columns-${activePersistenceKey}`);
        }
    }

    protected tryFindOwnerDialog() {
        if (this.domNode?.id == null)
            return;

        let owner = this.domNode.parentElement;
        while (owner != null) {
            const ownerWidget = tryGetWidget(owner, Widget);
            if (ownerWidget != null && ownerWidget instanceof EntityDialog || ownerWidget instanceof PropertyDialog) {
                this.ownerWidget = ownerWidget;
                break;
            }

            owner = owner.parentElement;
        }
    }

    protected getCriteriaFromParams() {
        if (this.options.criteriaText.length == 0)
            return null;

        const data = {
            ...this.ownerWidget?.["entity"],
            ...this.ownerWidget?.["getSaveEntity"]?.(),
        };

        const formattedText = microFormat(this.options.criteriaText, data, {
            handler: (token, data, _offset, _str) => {
                const value = data[token];
                if (value == null)
                    return "null";

                if (typeof value == "string") {
                    if (value.length == 0)
                        return "null";

                    return "'" + value.toString().replace("'", "''") + "'";
                }

                if (typeof value == "number")
                    return value.toString();

                if (typeof value == "boolean")
                    return value ? "1" : "0";

                return "'" + value.toString().replace("'", "''") + "'";
            }
        });

        try {
            const replaceNulls = [];

            const visit = (criteria: any[]) => {
                if (criteria.length == 3 && Array.isArray(criteria[2]) && criteria[2][0] == "null") {
                    const op = criteria[1];
                    replaceNulls.push({
                        field: criteria[0][0],
                        op
                    });

                    if (op == "=" || op == "<=")
                        return ["is null", criteria[0]];

                    if (op == "!=" || op == "<>" || op == ">")
                        return ["is not null", criteria[0]];

                    if (op == ">=")
                        return ["1", "=", "1"];

                    if (op == "<")
                        return ["1", "=", "0"];

                    return [criteria[0], criteria[1], null];
                }

                return criteria.map(x => Array.isArray(x) ? visit(x) : x);
            }

            this.formattedCriteria = formattedText;
            return visit(parseCriteria(formattedText));
        } catch (e) {
            this.formattedCriteria = null;
            return null;
        }
    }
}

